import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { BackArrowIcon, HighDemandIcon, TopRatingIcon, TrendingIcon } from '../icons';


import { GlobalStyles } from '@mui/material';
import ThreeDotMenu from '../components/university-table/ThreeDotMenu';
import UniversitySummary from '../components/university-table/UniversitySummary';
import parentsSpecializationListingManagementRows from '../components/ParentsSpecializationListingManagement/data';
const tagOptions = [
  { label: 'Trending', icon: <TrendingIcon fontSize="small" /> },
  { label: 'Top Rated', icon: <TopRatingIcon fontSize="small" /> },
  { label: 'High Demand', icon: <HighDemandIcon fontSize="small" /> },
];

const courseCategory = [
  { label: 'Management', },
  { label: 'Technology', },
  { label: 'Science', },
  { label: 'Commerce', },
  { label: 'Health & Medicine', },
  { label: 'Arts & Humanities', },
];

const UniversityParentsSpecializationListingManagement = ({ isHandlBtn = false }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();
  const [searchText, setSearchText] = React.useState('');
  const [selectedTag, setSelectedTag] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');

  React.useEffect(() => {
    setTimeout(() => {
      setRows(parentsSpecializationListingManagementRows);
      setLoading(false);
    }, 2000);
  }, []);

  const handleChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const matchesSearch =
      row.specId?.toLowerCase().includes(search) ||
      row.name?.toLowerCase().includes(search) ||
      row.university?.toLowerCase().includes(search) ||
      row.universityId?.toLowerCase().includes(search);

    const matchesTag = selectedTag ? row.tag === selectedTag : true;
    const matchesStatus = selectedStatus ? row.status === selectedStatus : true;

    return matchesSearch && matchesTag && matchesStatus;
  });

  function getStatusClass(status) {
    switch (status) {
      case 'Live':
        return 'status-live';
      case 'Draft':
        return 'status-draft';
      case 'Pending':
        return 'status-pending';
      case 'Deactivated':
        return 'status-deactivated';
      default:
        return '';
    }
  }





  const columns = [
    {
      field: 'serial',
      headerName: 'S. No.',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => filteredRows.findIndex((r) => r.id === params.row.id) + 1,
    },
    {
      field: 'specId',
      headerName: 'Spec. ID',
      flex: 1,
      minWidth: 130,
      cellClassName: 'sticky-col-1',
      headerClassName: 'sticky-col-1',
    },
    {
      field: 'name',
      headerName: 'Specialization Name',
      flex: 2,
      minWidth: 280,
      renderCell: (params) => {
        const { img, name } = params.row;

        const getInitials = (fullName) => {
          if (!fullName) return '';
          const words = fullName.trim().split(' ').filter(Boolean);
          if (words.length === 1) return words[0][0]?.toUpperCase() || '';
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
        };

        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase();
          return '#' + '00000'.substring(0, 6 - c.length) + c;
        };

        const bgColor = stringToColor(name || 'Course');

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/specialization-listing-management/parents-specialization')}
          >
            {img ? (
              <Box
                component="img"
                src={img}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(name)}
              </Box>
            )}
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'courseNameId',
      headerName: 'Course Name',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'university',
      headerName: 'University',
      flex: 2,
      minWidth: 260,
      renderCell: (params) => {
        const { logo, university, universityId } = params.row;
        const displayName = university;

        const getInitials = (fullName) => {
          if (!fullName) return '';
          const words = fullName.trim().split(' ').filter(Boolean);
          if (words.length === 1) return words[0][0]?.toUpperCase() || '';
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
        };

        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase();
          return '#' + '00000'.substring(0, 6 - c.length) + c;
        };

        const bgColor = stringToColor(displayName || 'University');

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
            {logo ? (
              <Box
                component="img"
                src={logo}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(displayName)}
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: '13px !important', fontWeight: 500 }}>
                {displayName}
              </Typography>
              {params.row.universityNote && (
                <Typography sx={{ fontSize: '11px !important', fontWeight: '500', color: '#363853' }}>
                  ({params.row.universityNote})
                </Typography>
              )}
              <Typography
                sx={{ fontSize: '11px !important', fontWeight: '500', color: '#6E7485', paddingTop: '4px' }}
              >
                University ID: {universityId}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={70} />
        ) : (
          <Chip
            label={params.value}
            size="small"
            icon={
              <FiberManualRecordIcon
                className={getStatusClass(params.value)}
                sx={{
                  fontSize: 10,
                  color: getStatusColor(params.value),
                }}
              />
            }
            sx={{
              bgcolor: getStatusBgColor(params.value),
              color: getStatusTextColor(params.value),
              fontWeight: 500,
            }}
          />
        ),
    },
    {
      field: 'tag',
      headerName: 'Tag',
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={100} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
            <Select
              value={params.value}
              onChange={(e) => handleChange(params.row.id, 'tag', e.target.value)}
              size="small"
              disabled={selectedStatus === 'Deactivated'}
              fullWidth
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                borderRadius: '50px',
                fontSize: '13px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '50px',
                },
                '& .MuiSelect-select': {
                  paddingY: '8px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {tagOptions.map((tag) => (
                <MenuItem key={tag.label} value={tag.label}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '13px' }}>
                    {tag.icon}
                    {tag.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      flex: 1,
      minWidth: 130,
    },
    {
      field: 'completion',
      headerName: 'Completion',
      width: 100,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value <= 41 ? '#FF4141' : params.value < 51 ? '#f59e0b' : '#22c55e',
            fontWeight: 500,
            fontSize: '13px',
          }}
        >
          {params.value}%
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) =>
        loading ? <Skeleton width={80} /> : <ThreeDotMenu rowId={params.row.id} onAction={handleChange} />,
    },
  ];


  function getStatusBgColor(status) {
    switch (status) {
      case 'Live':
        return '#fff';
      case 'Draft':
        return '#fff';
      case 'Pending':
        return '#fff';
      case 'Deactivated':
        return '#fff';
      default:
        return '#fff';
    }
  }

  function getStatusTextColor(status) {
    switch (status) {
      case 'Live':
        return '#363853'; // darker green
      case 'Draft':
        return '#363853'; // darker gray
      case 'Pending':
        return '#363853'; // darker orange
      case 'Deactivated':
        return '#363853'; // darker red
      default:
        return '#666';
    }
  }

  // Helper functions for status colors
  function getStatusColor(status) {
    switch (status) {
      case 'Live':
        return '#22c55e'; // green
      case 'Draft':
        return '#64748b'; // grayish
      case 'Pending':
        return '#f97316'; // orange
      case 'Deactivated':
        return '#ef4444'; // red
      default:
        return '#999';
    }
  }

  const statusList = [
    { label: 'Total Specialization', count: 24 },
    { label: 'Specialization Variant', count: 24 },
    { label: 'Archive', count: 24 },
    { label: 'Deactivated', count: 24 },
    { label: 'Draft', count: 10 },
  ];
  return (
    <>
      {/* Apply global styles to DataGrid dropdown menus */}
      <GlobalStyles
        styles={{
          '.MuiDataGrid-menuList': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiDataGrid-panelContent': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiSelect-select': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiList-root .MuiButtonBase-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-panelContent .MuiOutlinedInput-input': {
            fontSize: '13px',
          },
          '.MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiTypography-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root': {
            fontSize: '13px',
            padding: '6px 12px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root:hover': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-cell.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 2,
          },
          '& .MuiDataGrid-columnHeader.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 3, // slightly higher than cell
          },
        }}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff', }}>
          <div className="row align-items-center">
            <div className="col">
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1, }}><Link href="/university-listing-management/variant-course" className='d-flex'><BackArrowIcon /></Link>Parents Specialization Listing Management  </Typography>
            </div>
            {/* End Col */}
            {isHandlBtn && (<div className="col">  <Box display="flex" gap={2} sx={{ justifyContent: 'end', }}>
              <Button onClick={() => navigate('/specialization-listing-management/specialization-details')} sx={{ background: '#0463EF', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }}>
                Add Parents Specialization
              </Button>
            </Box></div>)}
          </div>
          {/* End Col */}
        </div>
        <UniversitySummary type="specialization" isAdmin />
        {/* Filter Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            my: 0,
            px: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Specialization Details</Typography>
            <Box
              sx={{
                fontSize: '12px',
                color: '#4b5563',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
              }}
            >
              <Typography component="span" sx={{ fontSize: '12px', }}>Showing</Typography>
              <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, }}>
                {filteredRows.length}
              </Typography>
              <Typography component="span">from</Typography>
              <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600 }}>
                {rows.length}
              </Typography>
              <Typography component="span" sx={{ fontSize: '12px', }}>results</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: '500px',
              marginLeft: 'auto',
            }}
          >
            <input
              type="text"
              placeholder="Search course name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 12px',
                borderRadius: '50px',
                border: '1px solid #ccc',
                height: '30px',
                fontSize: '12px',
              }}
            />
          </Box>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            displayEmpty
            size="small"
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              minWidth: 140,
              borderRadius: '50px',
              fontSize: '13px',
              height: '30px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '50px',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px' }}>
              All Status
            </MenuItem>
            <MenuItem value="Live" sx={{ fontSize: '13px' }}>
              Live
            </MenuItem>
            <MenuItem value="Draft" sx={{ fontSize: '13px' }}>
              Draft
            </MenuItem>
            <MenuItem value="Pending" sx={{ fontSize: '13px' }}>
              Pending
            </MenuItem>
            <MenuItem value="Deactivated" sx={{ fontSize: '13px' }}>
              Deactivated
            </MenuItem>
          </Select>
          <Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            displayEmpty
            size="small"
            sx={{
              minWidth: 160,
              borderRadius: '50px',
              height: '30px',
              fontSize: '13px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '50px',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px' }}>
              All Tags
            </MenuItem>
            {tagOptions.map((tag) => (
              <MenuItem key={tag.label} value={tag.label} sx={{ fontSize: '13px' }}>
                {tag.label}
              </MenuItem>
            ))}
          </Select>

        </Box>


        {/* Data Table */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            getRowHeight={() => 80}
            // getRowHeight={({ model }) => (model.status === 'Draft' ? 90 : 60)}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 20 } },
            }}
            pageSizeOptions={[20, 50]}
            checkboxSelection
            disableRowSelectionOnClick
            disableVirtualization
            sx={{
              border: 0,
              '& .MuiDataGrid-footerContainer': {
                justifyContent: 'center', // center pagination
                padding: '8px 16px', // display: 'none',
              },
              '& .MuiTablePagination-selectLabel': {
                marginBottom: '0',
              },
              '& .MuiTablePagination-root': {
                fontSize: '13px',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '12px',
                marginBottom: '0',
              },
              '& .MuiPaginationItem-root': {
                borderRadius: '8px',
                fontSize: '13px',
                minWidth: '32px',
                height: '32px',
              },
              '& .MuiTypography-root': {
                fontSize: '13px',
              },
              // '& .MuiDataGrid-virtualScroller': {
              //   overflow: 'auto',
              // },
              '& .MuiDataGrid-cell': {
                fontSize: '13px',
              },
              '& .MuiDataGrid-columnHeaders': {
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#fff',
              },
              '& .MuiDataGrid-cell.sticky-col-1': {
                position: 'sticky',
                left: 0,
                background: '#fff',
                zIndex: 2,
              },
              '& .MuiDataGrid-columnHeader.sticky-col-1': {
                position: 'sticky',
                left: 0,
                background: '#fff',
                zIndex: 99, // slightly higher than cell
              },

            }}
          />
        </Box>
      </Box>
    </>
  )
}

export default UniversityParentsSpecializationListingManagement
